import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Layout Components
import Navigation from './components/layout/Navigation';
import Breadcrumbs from './components/layout/Breadcrumbs';

// Admin Components
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminAPIKeys from './components/admin/AdminAPIKeys';
import AdminUsers from './components/admin/AdminUsers';
import AdminFoundersPanel from './components/admin/AdminFoundersPanel';
import AdminAuditLogs from './components/admin/AdminAuditLogs';
import AdminProducts from './components/admin/AdminProducts';
import AdminMatrix from './components/admin/AdminMatrix';
import AdminPageBuilder from './components/admin/AdminPageBuilder';
import AdminNetworkFeed from './components/admin/AdminNetworkFeed';
import AdminMLMDashboard from './components/admin/AdminMLMDashboard';
import ExternalAPIManager from './components/admin/ExternalAPIManager';
import DatabaseBackupManager from './components/admin/DatabaseBackupManager';
import AdminFAQ from './components/admin/AdminFAQ';
import AdminTemplateManagement from './components/admin/AdminTemplateManagement';
import AdminHoldingTank from './components/admin/AdminHoldingTank';
import AdminTierManagement from './components/admin/AdminTierManagement';
import MemberCreditsWallet from './components/member/MemberCreditsWallet';
import MemberNotifications from './components/member/MemberNotifications';
import TermsAndConditions from './components/legal/TermsAndConditions';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import QRCodeGenerator from './components/member/QRCodeGenerator';
import RookieCheatSheet from './components/member/RookieCheatSheet';
import OnboardingWizard from './components/member/OnboardingWizard';
import FoundersWaitlist from './components/public/FoundersWaitlist';
import FoundersLaunchPage from './components/public/FoundersLaunchPage';
import LaunchAccessPage from './components/public/LaunchAccessPage';
import WaitlistLaunchPage from './components/public/WaitlistLaunchPage';
import WaitlistMemberDashboard from './components/member/WaitlistMemberDashboard';
import AdminWaitlistConfig from './components/admin/AdminWaitlistConfig';
import FoundersDashboard from './components/member/FoundersDashboard';
import GamificationDashboard from './components/member/GamificationDashboard';
import GameDashboard from './components/member/GameDashboard';
import BootCampGuide from './components/member/BootCampGuide';
import AdMarketplace from './components/public/AdMarketplace';
import AdCreditsDashboard from './components/member/AdCreditsDashboard';
import RookieToolkit from './components/member/RookieToolkit';

// Public Components
import LandingPage from './components/public/LandingPage';
import MemberLogin from './components/public/MemberLogin';
import MemberRegister from './components/public/MemberRegister';
import FAQPage from './components/public/FAQPage';
import HoldingTankJoin from './components/public/HoldingTankJoin';
import HoldingTankDashboard from './components/public/HoldingTankDashboard';
import AdminNOWPayments from './components/admin/AdminNOWPayments';
import AdminProductsAllocations from './components/admin/AdminProductsAllocations';
import AdminMarketingManager from './components/admin/AdminMarketingManager';
import AdminConfigurationGuide from './components/admin/AdminConfigurationGuide';
import MemberWallet from './components/member/MemberWallet';
import ProductCatalog from './components/member/ProductCatalog';
import MemberDashboard from './components/member/MemberDashboard';
import PIFManager from './components/member/PIFManager';
import NetworkFeed from './components/member/NetworkFeed';
import PersonalPages from './components/member/PersonalPages';
import LinkInBioBuilder from './components/member/LinkInBioBuilder';
import LeadCaptureBuilder from './components/member/LeadCaptureBuilder';
import MLMMatrix from './components/member/MLMMatrix';
import PageBuilder from './components/member/PageBuilder';
import BootCampDashboard from './components/member/BootCampDashboard';
import MemberProfile from './components/member/MemberProfile';
import Marketplace from './components/member/Marketplace';
import CreditsAccess from './components/member/CreditsAccessDashboard';

// Lead Capture System Components
import TemplateMarketplace from './components/member/TemplateMarketplace';
import TemplateGenerator from './components/member/TemplateGenerator';
import LeadManagement from './components/member/LeadManagement';
import MarketingLibrary from './components/member/MarketingLibrary';
import SafeListMessaging from './components/member/SafeListMessaging';
import CanvaImporter from './components/member/CanvaImporter';
import BootCampTrainingVault from './components/member/BootCampTrainingVault';
import AdminTrainingVault from './components/admin/AdminTrainingVault';
import MyLeads from './components/member/MyLeads';
import FAQ from './components/shared/FAQ';

// Tool Ecosystem Components
import ToolStore from './components/member/ToolStore';
import ToolInventory from './components/member/ToolInventory';

// Mockup pages (visual concept screens for Touch 4 / onboarding videos — seeded demo data only)
import MockupIndex             from './components/mockups/MockupIndex';
import MockupMemberDashboard   from './components/mockups/MemberDashboard';
import MockupGlobalPifList     from './components/mockups/GlobalPifList';
import MockupPersonalPifQueue  from './components/mockups/PersonalPifQueue';
import MockupLinkFile          from './components/mockups/LinkFile';
import MockupRotator           from './components/mockups/Rotator';
import MockupSafeList          from './components/mockups/SafeList';
import MockupCreditWallet      from './components/mockups/CreditWallet';
import MockupTrainingVault     from './components/mockups/TrainingVault';
import MockupLeadCaptureTemplates from './components/mockups/LeadCaptureTemplates';
import MockupToolStore         from './components/mockups/ToolStore';

// Lead Capture Analytics (P1a) — admin overview at /admin/analytics/lead-capture
// and a member analytics page at /member/analytics/lead-capture. Widget
// component exists but is intentionally not mounted yet (post-launch dashboard).
import AdminLeadCaptureAnalytics  from './components/analytics/AdminLeadCaptureAnalytics';
import MemberLeadCaptureAnalytics from './components/analytics/MemberLeadCaptureAnalytics';

// Context and Auth imports
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Layout Wrapper Component
const LayoutWrapper = () => {
  return (
    <>
      <Navigation />
      <div className="md:pl-64">
        <div className="p-4 md:p-6">
          <Breadcrumbs />
          <div className="mt-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

// Protected Route Component for Admin
const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }
  
  if (!user || !['admin', 'moderator'].includes(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Protected Route Component for Members
const MemberProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'member') {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children, redirectAdmin = false, redirectMember = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }
  
  if (user) {
    if (['admin', 'moderator'].includes(user.role) && redirectAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (user.role === 'member' && redirectMember) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Public Routes (No Layout) */}
            {/* Pre-launch: root domain redirects to the waiting list funnel.
                Other internal routes (/login, /admin, /dashboard, /waitlist-dashboard,
                /access, /founders, etc.) are unaffected. */}
            <Route path="/" element={<Navigate to="/waitlist" replace />} />
            <Route path="/home" element={<LandingPage />} />
            <Route path="/join" element={<HoldingTankJoin />} />
            <Route path="/founders-waitlist" element={<FoundersWaitlist />} />
            <Route path="/join-founders" element={<FoundersWaitlist />} />
            <Route path="/founders" element={<FoundersLaunchPage />} />
            <Route path="/launch" element={<FoundersLaunchPage />} />
            <Route path="/launch/:code" element={<WaitlistLaunchPage />} />
            <Route path="/waitlist" element={<WaitlistLaunchPage />} />
            <Route path="/waitlist/:code" element={<WaitlistLaunchPage />} />
            <Route path="/waitlist-dashboard" element={<WaitlistMemberDashboard />} />

            {/* ----- Visual mockup back-office (admin-only, for video / onboarding use) -----
                Gated behind AdminProtectedRoute so the public cannot stumble onto DEMO data on
                the live domain. Non-admins are redirected to /admin/login. */}
            <Route path="/mockups"              element={<AdminProtectedRoute><MockupIndex /></AdminProtectedRoute>} />
            <Route path="/mockups/dashboard"     element={<AdminProtectedRoute><MockupMemberDashboard /></AdminProtectedRoute>} />
            <Route path="/mockups/global-pif"    element={<AdminProtectedRoute><MockupGlobalPifList /></AdminProtectedRoute>} />
            <Route path="/mockups/personal-pif"  element={<AdminProtectedRoute><MockupPersonalPifQueue /></AdminProtectedRoute>} />
            <Route path="/mockups/link-file"     element={<AdminProtectedRoute><MockupLinkFile /></AdminProtectedRoute>} />
            <Route path="/mockups/rotator"       element={<AdminProtectedRoute><MockupRotator /></AdminProtectedRoute>} />
            <Route path="/mockups/safe-list"     element={<AdminProtectedRoute><MockupSafeList /></AdminProtectedRoute>} />
            <Route path="/mockups/credits"       element={<AdminProtectedRoute><MockupCreditWallet /></AdminProtectedRoute>} />
            <Route path="/mockups/training"      element={<AdminProtectedRoute><MockupTrainingVault /></AdminProtectedRoute>} />
            <Route path="/mockups/lead-capture"  element={<AdminProtectedRoute><MockupLeadCaptureTemplates /></AdminProtectedRoute>} />
            <Route path="/mockups/tools"         element={<AdminProtectedRoute><MockupToolStore /></AdminProtectedRoute>} />

            {/* ----- Lead Capture Analytics (P1a) ----- */}
            <Route path="/admin/analytics/lead-capture"   element={<AdminProtectedRoute><AdminLeadCaptureAnalytics /></AdminProtectedRoute>} />
            <Route path="/member/analytics/lead-capture"  element={<MemberProtectedRoute><MemberLeadCaptureAnalytics /></MemberProtectedRoute>} />
            <Route path="/access" element={<LaunchAccessPage />} />
            <Route path="/launch-access" element={<LaunchAccessPage />} />
            <Route path="/holding/:userId" element={<HoldingTankDashboard />} />
            
            {/* Authentication Routes (No Layout) */}
            <Route 
              path="/login" 
              element={
                <PublicRoute redirectAdmin={true} redirectMember={true}>
                  <MemberLogin />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute redirectAdmin={true} redirectMember={true}>
                  <MemberRegister />
                </PublicRoute>
              } 
            />
            
            {/* Admin Login (No Layout) */}
            <Route 
              path="/admin/login" 
              element={
                <PublicRoute redirectAdmin={true}>
                  <AdminLogin />
                </PublicRoute>
              } 
            />
            
            {/* Routes with Layout */}
            <Route element={<LayoutWrapper />}>
              {/* Member Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <MemberProtectedRoute>
                    <MemberDashboard />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/member/dashboard" 
                element={
                  <MemberProtectedRoute>
                    <MemberDashboard />
                  </MemberProtectedRoute>
                } 
              />
          
              <Route 
                path="/pif" 
                element={
                  <MemberProtectedRoute>
                    <PIFManager />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/network" 
                element={
                  <MemberProtectedRoute>
                    <NetworkFeed />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/personal-pages" 
                element={
                  <MemberProtectedRoute>
                    <PersonalPages />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/link-in-bio" 
                element={
                  <MemberProtectedRoute>
                    <LinkInBioBuilder />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/capture-builder" 
                element={
                  <MemberProtectedRoute>
                    <LeadCaptureBuilder />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route 
                path="/matrix" 
                element={
                  <MemberProtectedRoute>
                    <MLMMatrix />
                  </MemberProtectedRoute>
                } 
              />
              
              <Route path="/wallet" element={<MemberProtectedRoute><MemberWallet /></MemberProtectedRoute>} />
              <Route path="/pages" element={<MemberProtectedRoute><PageBuilder /></MemberProtectedRoute>} />
              <Route path="/products" element={<MemberProtectedRoute><ProductCatalog /></MemberProtectedRoute>} />
              <Route path="/bootcamp" element={<MemberProtectedRoute><BootCampDashboard /></MemberProtectedRoute>} />
              <Route path="/profile" element={<MemberProtectedRoute><MemberProfile /></MemberProtectedRoute>} />
              <Route path="/marketplace" element={<MemberProtectedRoute><Marketplace /></MemberProtectedRoute>} />
              <Route path="/credits" element={<MemberProtectedRoute><CreditsAccess /></MemberProtectedRoute>} />
              
              {/* Lead Capture System Routes */}
              <Route path="/member/lead-capture/marketplace" element={<MemberProtectedRoute><TemplateMarketplace /></MemberProtectedRoute>} />
              <Route path="/member/lead-capture/generate" element={<MemberProtectedRoute><TemplateGenerator /></MemberProtectedRoute>} />
              <Route path="/member/lead-capture/leads" element={<MemberProtectedRoute><LeadManagement /></MemberProtectedRoute>} />
              <Route path="/member/marketing-library" element={<MemberProtectedRoute><MarketingLibrary /></MemberProtectedRoute>} />
              
              {/* Safe List Messaging Routes */}
              <Route path="/member/safe-list" element={<MemberProtectedRoute><SafeListMessaging userRole="member" /></MemberProtectedRoute>} />
              <Route path="/admin/safe-list" element={<AdminProtectedRoute><SafeListMessaging userRole="admin" /></AdminProtectedRoute>} />
              
              {/* QR Code Generator Route */}
              <Route 
                path="/qr-generator" 
                element={
                  <MemberProtectedRoute>
                    <QRCodeGenerator />
                  </MemberProtectedRoute>
                } 
              />
              
              {/* Rookie Cheat Sheet Route */}
              <Route 
                path="/rookie-cheat-sheet" 
                element={
                  <MemberProtectedRoute>
                    <RookieCheatSheet />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/member/rookie-cheat-sheet" 
                element={
                  <MemberProtectedRoute>
                    <RookieCheatSheet />
                  </MemberProtectedRoute>
                } 
              />
              
              {/* Founders Dashboard Route */}
              <Route 
                path="/member/founders" 
                element={
                  <MemberProtectedRoute>
                    <FoundersDashboard />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/founders-dashboard" 
                element={
                  <MemberProtectedRoute>
                    <FoundersDashboard />
                  </MemberProtectedRoute>
                } 
              />
              
              {/* Gamification Dashboard Route */}
              <Route 
                path="/member/gamification" 
                element={
                  <MemberProtectedRoute>
                    <GamificationDashboard />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/gamification" 
                element={
                  <MemberProtectedRoute>
                    <GamificationDashboard />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/game" 
                element={
                  <MemberProtectedRoute>
                    <GameDashboard />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/member/boot-camp-guide" 
                element={
                  <MemberProtectedRoute>
                    <BootCampGuide />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/member/ad-credits" 
                element={
                  <MemberProtectedRoute>
                    <AdCreditsDashboard />
                  </MemberProtectedRoute>
                } 
              />
              <Route 
                path="/member/rookie-toolkit" 
                element={
                  <MemberProtectedRoute>
                    <RookieToolkit />
                  </MemberProtectedRoute>
                } 
              />
              
              {/* Public Ad Marketplace */}
              <Route path="/marketplace" element={<AdMarketplace />} />
              
              {/* Canva Import Routes */}
              <Route path="/member/canva-import" element={<MemberProtectedRoute><CanvaImporter /></MemberProtectedRoute>} />
              
              {/* Training Vault Routes */}
              <Route path="/member/training-vault" element={<MemberProtectedRoute><BootCampTrainingVault /></MemberProtectedRoute>} />
              <Route path="/member/training-courses" element={<MemberProtectedRoute><BootCampTrainingVault /></MemberProtectedRoute>} />
              <Route path="/admin/training-vault" element={<AdminProtectedRoute><AdminTrainingVault /></AdminProtectedRoute>} />
              
              {/* Admin Founders Panel */}
              <Route path="/admin/founders" element={<AdminProtectedRoute><AdminFoundersPanel /></AdminProtectedRoute>} />
              <Route path="/admin/waitlist" element={<AdminProtectedRoute><AdminWaitlistConfig /></AdminProtectedRoute>} />
              <Route path="/admin/tiers" element={<AdminProtectedRoute><AdminTierManagement /></AdminProtectedRoute>} />

              {/* P1: Member wallet & notifications */}
              <Route path="/member/credits-wallet" element={
                <MemberProtectedRoute>
                  <div className="container mx-auto py-6 px-4 max-w-6xl">
                    <MemberCreditsWallet />
                  </div>
                </MemberProtectedRoute>
              } />
              <Route path="/member/notifications" element={
                <MemberProtectedRoute>
                  <div className="container mx-auto py-6 px-4 max-w-4xl">
                    <MemberNotifications />
                  </div>
                </MemberProtectedRoute>
              } />
              
              {/* Tool Ecosystem Routes */}
              <Route path="/member/tools/store" element={<MemberProtectedRoute><ToolStore /></MemberProtectedRoute>} />
              <Route path="/member/tools/inventory" element={<MemberProtectedRoute><ToolInventory /></MemberProtectedRoute>} />
              
              {/* Lead Management Routes */}
              <Route path="/member/lead-capture/leads" element={<MemberProtectedRoute><MyLeads /></MemberProtectedRoute>} />
              
              {/* FAQ Route */}
              <Route path="/faq" element={<FAQ />} />
              
              {/* Legal Routes */}
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              
              {/* Protected Admin Routes */}
              <Route 
                path="/admin/dashboard" 
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/api-keys" 
                element={
                  <AdminProtectedRoute>
                    <AdminAPIKeys />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <AdminProtectedRoute>
                    <AdminUsers />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/audit-logs" 
                element={
                  <AdminProtectedRoute>
                    <AdminAuditLogs />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/products" 
                element={
                  <AdminProtectedRoute>
                    <AdminProductsAllocations />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/matrix" 
                element={
                  <AdminProtectedRoute>
                    <AdminMatrix />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/page-builder" 
                element={
                  <AdminProtectedRoute>
                    <AdminPageBuilder />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/network-feed" 
                element={
                  <AdminProtectedRoute>
                    <AdminNetworkFeed />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/mlm-dashboard" 
                element={
                  <AdminProtectedRoute>
                    <AdminMLMDashboard />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/external-api" 
                element={
                  <AdminProtectedRoute>
                    <ExternalAPIManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/database-backup" 
                element={
                  <AdminProtectedRoute>
                    <DatabaseBackupManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/faq" 
                element={
                  <AdminProtectedRoute>
                    <AdminFAQ />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/nowpayments" 
                element={
                  <AdminProtectedRoute>
                    <AdminNOWPayments />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/allocations" 
                element={
                  <AdminProtectedRoute>
                    <AdminProductsAllocations />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/marketing" 
                element={
                  <AdminProtectedRoute>
                    <AdminMarketingManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/configuration" 
                element={
                  <AdminProtectedRoute>
                    <AdminConfigurationGuide />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/templates" 
                element={
                  <AdminProtectedRoute>
                    <AdminTemplateManagement />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/holding-tank" 
                element={
                  <AdminProtectedRoute>
                    <AdminHoldingTank />
                  </AdminProtectedRoute>
                } 
              />
            </Route>
          </Routes>
        </div>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;